import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import data from "../content/case-studies.json";
import { SERVICES, SERVICE_INTROS } from "../content/services";
import { CaseCard } from "../components/CaseCard";
import { CTABand } from "../components/Buttons";
import { useMeta } from "../hooks/useMeta";

export default function Home({ serviceKey = "all" }) {
  const service = SERVICES.find((s) => s.key === serviceKey);
  const intro = SERVICE_INTROS[serviceKey];
  const [filter, setFilter] = useState(service ? service.name : "All");

  useEffect(() => {
    setFilter(service && service.key !== "all" ? service.name : "All");
  }, [serviceKey, service]);

  useMeta({
    title: intro
      ? `${intro.titleBefore}${intro.titleBold}${intro.titleAfter} | Smart Marketer Agency results`
      : "Smart Marketer Agency results. Real accounts, real numbers, real profit.",
    description: intro
      ? intro.intro
      : "Case studies with real numbers: spend, revenue, contribution margin. Proof that Smart Marketer Agency makes brands more profitable.",
  });

  const visible = useMemo(() => {
    const list = data.cases.filter(
      (c) => c.status !== "draft" && (filter === "All" || c.services.includes(filter))
    );
    const published = list
      .filter((c) => c.status === "published")
      .sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0) || (b.publishedAt || "").localeCompare(a.publishedAt || ""));
    const soon = list.filter((c) => c.status === "coming-soon");
    return [...published, ...soon];
  }, [filter]);

  return (
    <main data-testid={serviceKey === "all" ? "home-page" : `service-page-${serviceKey}`} className="bg-white">
      <section id="results" data-testid="results-grid-section">
        <div className="mx-auto max-w-6xl px-5 pb-16 pt-10 md:pb-20 md:pt-14">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <h1 className="text-[26px] font-normal leading-[1.2] text-navy md:text-[34px]">
              {intro ? (
                <>{intro.titleBefore}<strong className="font-bold">{intro.titleBold}</strong>{intro.titleAfter}</>
              ) : (
                <>Case studies. <strong className="font-bold">Real accounts, real numbers.</strong></>
              )}
            </h1>
            <p data-testid="home-intro" className="mt-3 max-w-2xl text-sm leading-relaxed text-navy/60 md:text-base">
              {intro ? intro.intro : "Pick a service or scroll. Every case shows spend, revenue and what happened to the business."}
            </p>
          </motion.div>
          <div data-testid="filter-chips" className="mt-8 flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button
                key={s.key}
                data-testid={`filter-chip-${s.key}`}
                onClick={() => setFilter(s.name)}
                className={`rounded-full px-5 py-2 text-sm font-bold transition-colors duration-200 ${
                  filter === s.name ? "bg-navy text-white" : "bg-fog text-navy/60 hover:text-navy"
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
          <div className="mt-8 grid gap-5 md:grid-cols-2">
            {visible.map((cs) => (
              <CaseCard key={cs.slug} cs={cs} featured={cs.featured && cs.status === "published"} />
            ))}
          </div>
          {visible.length === 0 && (
            <p className="mt-8 text-navy/60">Cases for this service are being written. Check back soon.</p>
          )}
        </div>
      </section>

      <CTABand />
    </main>
  );
}
